// Главная: лендинг tez-hop.uz (ТЗ hero)
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BrandFooter from '../components/BrandFooter';
import { logoPng } from '../config/assets';
import { STAFF_PWA_COURIER, STAFF_PWA_KEY } from '../lib/staffPwa';
import { DEMO_HUB_HREF, DEMO_LOGIN_PATH, DEMO_MENU_PATH } from '../config/demo';
import './LandingPage.css';

const base = process.env.PUBLIC_URL || '';

function hasAuthToken() {
  try {
    return !!(
      localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')
    );
  } catch {
    return false;
  }
}

function isStandalonePwa() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if ('standalone' in window.navigator && (window.navigator).standalone) return true;
  return false;
}

const COPY = {
  ru: {
    metaDescription:
      'Цифровое QR меню для ресторанов, отелей и баров. Система управления заказами. Создайте электронное меню для вашего заведения.',
    titleLine1: 'Цифровое',
    titleAccent: 'QR',
    titleLine2: 'меню',
    subtitle: 'Для ресторанов, отелей, баров',
    tagline: '+ Система управления заказами',
    demoBtn: 'Demo menu',
    adminBtn: 'Admin demo',
    logoAlt: 'tez-hop',
    mockMenuAlt: 'Пример QR-меню для гостя',
    mockAdminAlt: 'Панель управления заказами',
    langLabel: 'Язык',
    footerNav: [
      { href: '/', label: 'Главная' },
      { href: DEMO_HUB_HREF, label: 'Demo (QR)' },
      { href: DEMO_LOGIN_PATH, label: 'Вход' },
    ],
    headerSignIn: 'Вход',
    headerCourier: 'Курьер',
  },
  uz: {
    metaDescription:
      'Restoranlar, mehmonxonalar va barlar uchun raqamli QR menyu. Buyurtmalarni boshqarish tizimi.',
    titleLine1: 'Raqamli',
    titleAccent: 'QR',
    titleLine2: 'menyu',
    subtitle: 'Restoranlar, mehmonxonalar, barlar uchun',
    tagline: '+ Buyurtmalarni boshqarish tizimi',
    demoBtn: 'Demo menyu',
    adminBtn: 'Admin demo',
    logoAlt: 'tez-hop',
    mockMenuAlt: 'QR menyu namunasi',
    mockAdminAlt: 'Buyurtmalar paneli',
    langLabel: 'Til',
    footerNav: [
      { href: '/', label: 'Bosh sahifa' },
      { href: DEMO_HUB_HREF, label: 'Demo (QR)' },
      { href: DEMO_LOGIN_PATH, label: 'Kirish' },
    ],
    headerSignIn: 'Kirish',
    headerCourier: 'Kuryer',
  },
  en: {
    metaDescription:
      'Digital QR menu for restaurants, hotels and bars. Order management system for your venue.',
    titleLine1: 'Digital',
    titleAccent: 'QR',
    titleLine2: 'menu',
    subtitle: 'For restaurants, hotels, bars',
    tagline: '+ Order management system',
    demoBtn: 'Demo menu',
    adminBtn: 'Admin demo',
    logoAlt: 'tez-hop',
    mockMenuAlt: 'Guest QR menu example',
    mockAdminAlt: 'Orders admin panel',
    langLabel: 'Language',
    footerNav: [
      { href: '/', label: 'Home' },
      { href: DEMO_HUB_HREF, label: 'Demo (QR)' },
      { href: DEMO_LOGIN_PATH, label: 'Sign in' },
    ],
    headerSignIn: 'Sign in',
    headerCourier: 'Courier',
  },
};

const LANGS = [
  { code: 'ru', flag: 'flag-ru.svg', label: 'RU' },
  { code: 'uz', flag: 'flag-uz.svg', label: 'UZ' },
  { code: 'en', flag: 'flag-en.svg', label: 'EN' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [lang, setLang] = useState('ru');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const t = COPY[lang] || COPY.ru;

  // Иконка «Домой» с манифеста главной (start_url /) — без редиректа; помечаем курьера и ведём на вход
  useLayoutEffect(() => {
    if (!isStandalonePwa() || hasAuthToken()) return;
    if (localStorage.getItem(STAFF_PWA_KEY) === STAFF_PWA_COURIER) {
      navigate('/login?hint=courier', { replace: true });
    }
  }, [navigate]);

  const closeLang = useCallback(() => setLangOpen(false), []);

  useEffect(() => {
    const onDoc = (e) => {
      if (!langRef.current?.contains(e.target)) closeLang();
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [closeLang]);

  useEffect(() => {
    document.documentElement.lang = lang === 'en' ? 'en' : lang === 'uz' ? 'uz' : 'ru';
  }, [lang]);

  useEffect(() => {
    const m = document.querySelector('meta[name="description"]');
    const prev = m?.getAttribute('content');
    if (m) m.setAttribute('content', t.metaDescription);
    return () => {
      if (m && prev != null) m.setAttribute('content', prev);
    };
  }, [t.metaDescription]);

  const currentFlag = LANGS.find((l) => l.code === lang)?.flag || 'flag-ru.svg';

  return (
    <div className="landing-page">
      <header className="landing-page__header">
        <div className="landing-page__header-inner">
          <a href="/" className="landing-page__logo" aria-label="tez-hop.uz">
            <img
              className="landing-page__logo-img"
              src={logoPng()}
              alt={t.logoAlt}
              width={60}
              height={60}
            />
            <span className="landing-page__logo-text">tez-hop.uz</span>
          </a>
          <div className="landing-page__header-end">
            <div className="landing-page__header-right">
              <Link className="landing-page__header-link" to="/login">
                {t.headerSignIn}
              </Link>
              <Link
                className="landing-page__header-link landing-page__header-link--courier"
                to="/login?hint=courier"
              >
                {t.headerCourier}
              </Link>
            </div>
            <div className="landing-page__lang" ref={langRef}>
            <button
              type="button"
              className="landing-page__lang-btn"
              aria-expanded={langOpen}
              aria-haspopup="listbox"
              aria-label={t.langLabel}
              onClick={(e) => {
                e.stopPropagation();
                setLangOpen((v) => !v);
              }}
            >
              <img
                className="landing-page__flag"
                src={`${base}/landing/images/${currentFlag}`}
                alt=""
                width={42}
                height={28}
              />
            </button>
            {langOpen && (
              <ul className="landing-page__lang-dropdown" role="listbox">
                {LANGS.map(({ code, flag, label }) => (
                  <li key={code} role="none">
                    <button
                      type="button"
                      role="option"
                      aria-selected={lang === code}
                      onClick={() => {
                        setLang(code);
                        setLangOpen(false);
                      }}
                    >
                      <img
                        className="landing-page__flag"
                        src={`${base}/landing/images/${flag}`}
                        alt=""
                        width={36}
                        height={24}
                      />
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            </div>
          </div>
        </div>
      </header>

      <main className="landing-page__hero">
        <div className="landing-page__hero-inner">
          <div className="landing-page__mockup landing-page__mockup--left">
            <img
              className="landing-page__mockup-img"
              src={`${base}/landing/images/mockup-menu.png`}
              alt={t.mockMenuAlt}
              width={720}
              height={1400}
              loading="lazy"
            />
          </div>

          <div className="landing-page__content">
            <h1 className="landing-page__title">
              {t.titleLine1}{' '}
              <span className="landing-page__title-accent">{t.titleAccent}</span>{' '}
              {t.titleLine2}
            </h1>
            <h2 className="landing-page__subtitle">{t.subtitle}</h2>
            <p className="landing-page__description">{t.tagline}</p>
            <div className="landing-page__buttons">
              <Link to={DEMO_MENU_PATH} className="landing-page__btn landing-page__btn--demo">
                {t.demoBtn}
              </Link>
              <Link to={DEMO_LOGIN_PATH} className="landing-page__btn landing-page__btn--admin">
                {t.adminBtn}
              </Link>
            </div>
          </div>

          <div className="landing-page__mockup landing-page__mockup--right">
            <img
              className="landing-page__mockup-img"
              src={`${base}/landing/images/mockup-admin.png`}
              alt={t.mockAdminAlt}
              width={880}
              height={600}
              loading="lazy"
            />
          </div>
        </div>
      </main>

      <BrandFooter
        className="brand-footer--on-landing"
        navItems={t.footerNav}
      />
    </div>
  );
}
